import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {
  RelativeDateTimeFormatsEnum,
  RelativeDateTimeFormatter,
} from '../../../../../srcCommon/formatters/dateTime/relativeDateTimeFormatter';
import {Paragraph} from '../../../../components/common/paragraph';
import {ApiApplicationAuthorizationUrl} from '../../../../models/api/applicationAuthorizationUrlModel';
import {OAuth2ApplicationCredentials} from '../../../../models/api/applicationCredentialsModel';
import {ApplicationModel} from '../../../../models/api/applicationModel';
import {openElectronAuthPopup} from '../../../../services/electron/electronBridgeOutbound';
import {postApiAsync} from '../../../../services/http/http';
import {openExternalPopupUrl} from '../../../../services/popups';

/*
 * Props.
 */

interface ApplicationCredentialsOauthInputsProps {
  installedApplication: ApplicationModel;
  credentials: OAuth2ApplicationCredentials;
  isReadOnly: boolean;
  serverOrigin: string;
}

/*
 * Style.
 */

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  margin-top: 20px;
`;

/*
 * Component.
 */

export const ApplicationCredentialsOAuthInputs: FC<ApplicationCredentialsOauthInputsProps> = ({
  installedApplication,
  credentials,
  isReadOnly,
  serverOrigin,
}) => {
  const triggerOAuthFlow = async () => {
    const authorization = await postApiAsync(
      ApiApplicationAuthorizationUrl,
      `/accounts/oauth/applications/${installedApplication.uid}`,
      {origin: serverOrigin},
    );

    // If we are on Electron, the authorization URL needs to be open via the browser.
    if (openElectronAuthPopup(authorization.url)) {
      return;
    }

    openExternalPopupUrl(authorization.url);
  };

  return (
    <>
      {maybeRenderCredentialsInfo(credentials)}
      <StyledButtonWrapperDiv>
        <Button
          variant={ButtonVariantsEnum.SECONDARY}
          size={VisualSizesEnum.MEDIUM}
          isRounded
          isDisabled={isReadOnly}
          onClick={triggerOAuthFlow}
        >
          <Trans id="app-oauth-credentials-authorize">Sign in</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </>
  );
};

function maybeRenderCredentialsInfo(credentials: OAuth2ApplicationCredentials) {
  if (!credentials.access_token) {
    return null;
  }

  return (
    <Paragraph>
      <Trans id="app-oauth-signed-in-timeago">
        Signed in{' '}
        <RelativeDateTimeFormatter value={credentials.createdAt} format={RelativeDateTimeFormatsEnum.FULL} />
      </Trans>
    </Paragraph>
  );
}
